import { useState } from 'react'
import { cn } from '../../lib/cn.js'
import { Button } from '../ui/Button.js'
import { Text } from '../ui/Typography.js'

export interface SecretCodesProps {
  /** One code, or ten. The authenticator secret and the recovery codes are both this. */
  codes: string[]
  /** What they are. "Recovery codes", "Setup key". */
  label: string
  /** How many characters to a group. Base32 secrets read in fours. */
  groupOf?: number
  /**
   * The line under them, when the default one is wrong.
   *
   * This component is used twice on the enrolment screen and the sentence
   * underneath was written for only one of them: *"Keep these somewhere you
   * can reach without this laptop and without your phone."* True of ten
   * recovery codes. Not true of the **set-up key**, which is not a spare way
   * in — it is the QR code in characters, for a camera that will not read, and
   * it is useless the moment the app has it.
   */
  note?: React.ReactNode
  className?: string
}

/**
 * `JBSWY3DPEHPK3PXP` → `JBSW Y3DP EHPK 3PXP`. Left alone when it is grouped already.
 *
 * *Any* separator counts, not only a space. A recovery code is
 * `26DB0-78C72` — five hex, a hyphen, five hex — and this tested for
 * whitespace only, so it chopped it into `26DB 0-78 C72` and cut the hyphen
 * loose in the middle of a group. Eight codes, each read off a screen and
 * typed by somebody who has just lost their phone, all regrouped against
 * their own shape. Seen the first time the enrolment screen was opened in a
 * browser.
 */
const grouped = (code: string, size: number) =>
  /[\s-]/.test(code) ? code : (code.match(new RegExp(`.{1,${size}}`, 'g')) ?? [code]).join(' ')

/**
 * Something shown once and never again.
 *
 * The enrolment response is three fields and the contract says what they have in
 * common in as many words: *"Shown once, at enrolment, and never retrievable
 * again."* The QR is one of them and has its own component. The other two — the
 * setup key to type when a camera will not read, and the ten recovery codes — are
 * the same thing in different quantities, so they are one component used twice.
 *
 * **Grouped, because these are read off a screen and typed by hand.** A
 * thirty-two character base32 secret in one run is a string nobody transcribes
 * correctly; in fours it is eight short words.
 *
 * **Copy and print, because this is the moment.** There is no second chance at
 * this screen, and a recovery code that only exists in a browser tab somebody is
 * about to close is not a recovery code. Printing matters more than it sounds: it
 * is the one copy that survives losing the phone *and* the laptop, which is the
 * situation recovery codes exist for.
 *
 * It says so plainly rather than trusting the person to infer it — on the one
 * screen where getting this wrong locks the client's team out of their own
 * platform.
 */
export function SecretCodes({ codes, label, groupOf = 4, note, className }: SecretCodesProps) {
  const [copied, setCopied] = useState(false)
  const text = codes.join('\n')

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // A refused clipboard is not an error worth a banner: the codes are on the
      // screen and can be selected. Saying "Copied" when nothing was would be worse.
      setCopied(false)
    }
  }

  return (
    <section className={cn('flex flex-col gap-3 rounded-md border border-hairline p-4', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <Text variant="body-strong" as="h3">
          {label}
        </Text>
        <Text variant="caption" className="text-ink-muted">
          Shown once
        </Text>
      </div>

      {/* Two columns for ten recovery codes, one for a single set-up key.
          A fixed two dropped a thirty-two character key into half the width
          and wrapped it mid-group, which is the one thing the grouping above
          exists to prevent — somebody transcribing it by hand loses their
          place at the fold. */}
      <ul className={cn('grid grid-cols-1 gap-1', codes.length > 1 && 'sm:grid-cols-2')}>
        {codes.map((code) => (
          <li key={code} className="font-mono text-second tracking-wide text-ink tabular-nums">
            {grouped(code, groupOf)}
          </li>
        ))}
      </ul>

      <Text variant="caption" className="text-ink-muted">
        {/* The sentence is the component's whole job as much as the list is. */}
        {note ??
          'Keep these somewhere you can reach without this laptop and without your phone. They will not be shown again.'}
      </Text>

      <div className="flex gap-2 print:hidden">
        <Button variant="secondary" size="sm" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button variant="tertiary" size="sm" onClick={() => window.print()}>
          Print
        </Button>
      </div>
    </section>
  )
}
